import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LoginComponent} from './modules/login/login.component';
import {AccountConfirmComponent} from './modules/account/account-confirm/account-confirm.component';
import {
  AccountAlreadyConfirmedComponent
} from './modules/account/account-already-confirmed/account-already-confirmed.component';
import {AccountErrorComponent} from './modules/account/account-error/account-error.component';
import {AccountCreatedComponent} from './modules/account/account-created/account-created.component';
import {AuthGuard} from './modules/common/services/authorization/authGuard';
import {MaintenancePageComponent} from './modules/common/maintenance-page/maintenance-page.component';
import {ResetPasswordComponent} from './modules/account/reset-password/reset-password.component';
import {ForbiddenPageComponent} from './modules/common/forbidden-page/forbidden-page.component';
import {HomeComponent} from './home/home.component';
import {AlreadyLoggedGuard} from './modules/common/services/authorization/already-logged.guard';
import {
  AlreadyLoggedLoginComponentGuard
} from './modules/common/services/authorization/AlreadyLoggedLoginComponentGuard';
import {FeedbackComponent} from './modules/feedback/feedback.component';
import {RegisterComponent} from './modules/register/register.component';
import {PermissionsGuard} from './modules/common/services/authorization/permissionsGuard';
import {NotFoundComponent} from './modules/common/components/not-found/not-found.component';
import {ErrorPageComponent} from '@npm.chapps.com/chapps.common.angular.library';
import {Permission} from './modules/common/enums/enums';
import {LicenseComponent} from "./modules/license/license.component";

const routes: Routes = [
  { path: '', redirectTo: '/account', pathMatch: 'full' },
  { path: '', loadChildren: () => import('@npm.chapps.com/chapps.common.angular.library').then(m => m.ChappsCommonAngularLibraryModule)},
  { path: 'home', component: HomeComponent, canActivate: [AuthGuard]},
  { path: 'account', component: LoginComponent, canActivate: [AlreadyLoggedLoginComponentGuard]},
  { path: 'register', component: RegisterComponent, canActivate: [AlreadyLoggedGuard]},
  {
    path: 'accounts',
    loadChildren: () => import('./modules/accounts/accounts.module').then(m => m.AccountsModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.USERMANAGEMENT]}
  },
  {
    path: 'payment',
    loadChildren: () => import('./modules/payment/payment.module').then(m => m.PaymentModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.PAYMENT]}
  },
  {
    path: 'partners',
    loadChildren: () => import('./modules/partners/partners.module').then(m => m.PartnersModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.PARTNERMANAGEMENT]}
  },
  {
    path: 'sites',
    loadChildren: () => import('./modules/sites/sites.module').then(m => m.SitesModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.BUILDINGSVIEW]}
  },
  {
    path: 'buildings',
    loadChildren: () => import('./modules/buildings/buildings.module').then(m => m.BuildingsModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.BUILDINGSVIEW]}
  },
  {
    path: 'issues',
    loadChildren: () => import('./modules/issues/issues.module').then(m => m.IssuesModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'maintenance',
    loadChildren: () => import('./modules/maintenance/maintenance.module').then(m => m.MaintenanceModule),
    canActivate: [AuthGuard],
  },
  { path: 'license', component: LicenseComponent, canActivate: [AuthGuard] },
  { path: 'account-confirm/:culture/:confirmationCode', component: AccountConfirmComponent },
  { path: 'account-confirm/:culture', redirectTo: '' },
  { path: 'account-already-confirmed', component: AccountAlreadyConfirmedComponent },
  { path: 'account-error', component: AccountErrorComponent },
  { path: 'account-created', component: AccountCreatedComponent},
  { path: 'error-page', component: ErrorPageComponent },
  { path: 'not-found', component: NotFoundComponent },
  { path: 'forbidden-page', component: ForbiddenPageComponent },
  { path: 'maintenance-page', component: MaintenancePageComponent},
  { path: 'feedback', component: FeedbackComponent, canActivate: [AuthGuard]},
  { path: 'executors', loadChildren:
    () => import('./modules/executors/executors.module').then(m => m.ExecutorsModule), canActivate: [AuthGuard]},
  { path: 'roles', loadChildren:
    () => import('./modules/roles/roles.module').then(m => m.RolesModule), canActivate: [AuthGuard]},
  {
    path: 'report-settings',
    loadChildren: () => import('./modules/report-settings/report-settings.module').then(m => m.ReportSettingsModule),
    canActivate: [AuthGuard]
  },
  { path: 'reset-password/:culture/:resetPasswordCode', component: ResetPasswordComponent, canActivate: [AuthGuard] },
  { path: 'business', loadChildren:
    () => import('./modules/business/business.module').then(m => m.BusinessModule), canActivate: [AuthGuard]},
  {
    path: 'devices',
    loadChildren: () => import('./modules/devices/devices.module').then(m => m.DevicesModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.DEVICESVIEW]}
  },
  {
    path: 'contacts',
    loadChildren: () => import('./modules/contacts/contacts.module').then(m => m.ContactsModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: { permissions: [Permission.CONTACTSVIEW]}
  },
  {
    path: 'export',
    loadChildren: () => import('./modules/export/export.module').then(m => m.ExportModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.CONTACTSVIEW]}
  },
  {
    path: 'import',
    loadChildren: () => import('./modules/import/import.module').then(m => m.ImportModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.CONTACTSEDIT]}
  },
  { path: 'subscription', loadChildren:
    () => import('./modules/subscription/subscription.module').then(m => m.SubscriptionModule), canActivate: [AuthGuard]},
  {
    path: 'inspections',
    loadChildren: () => import('./modules/inspections/inspections.module').then(m => m.InspectionsModule),
    canActivate: [AuthGuard, PermissionsGuard],
    data: {permissions : [Permission.INSPECTIONSVIEW]}
  },
  { path: '**', redirectTo: '/account'}
];


@NgModule({
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
